// src/components/admin_panel/AdminSubscriptions.js
import React, { useState, useEffect } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { adminApi } from "../../services/adminApi";
import SourceGroupMappingsNeedsReview from "./SourceGroupMappingsNeedsReview";

function formatDate(unixSeconds) {
  if (!unixSeconds) return "—";
  const date = new Date(unixSeconds * 1000);
  if (isNaN(date.getTime())) return "—";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
}

const CURRENCY_SYMBOLS = { GBP: "£", USD: "$", EUR: "€" };

function formatAmount(s) {
  if (s.amount == null) return "—";
  const symbol = CURRENCY_SYMBOLS[s.currency] || `${s.currency || ""} `;
  const perInterval = s.interval ? `/${s.interval}` : "";
  return `${symbol}${s.amount.toFixed(2)}${perInterval}`;
}

// Raw Stripe status ("past_due" etc.) plus the extra context Stripe's own
// dashboard shows (scheduled cancellation, trial end) that the bare status
// field alone doesn't convey.
function formatStatus(s) {
  const label = (s.status || "")
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

  if (s.status === "trialing" && s.trial_end) {
    return `${label} (ends ${formatDate(s.trial_end)})`;
  }
  if ((s.status === "active" || s.status === "trialing") && s.cancel_at_period_end) {
    return `${label} (cancels ${formatDate(s.current_period_end)})`;
  }
  return label;
}

// landing_page_type/url_slug are resolved two ways (see
// get_admin_subscriptions's attribution_source doc): "stripe" metadata
// recorded durably at purchase time, or - only for subscriptions with no
// such metadata at all - a "mixpanel" fallback estimated by matching the
// customer's email against a Mixpanel Engage profile. The latter is a
// best-effort guess, not a purchase-time fact, so mark it rather than
// showing it identically to the definitive case.
function AttributionValue({ value, source }) {
  if (!value) return "—";
  if (source !== "mixpanel") return value;
  return (
    <>
      {value}
      <span
        title="Estimated from Mixpanel - this subscription predates purchase-time attribution metadata, so this is a best-effort match on customer email rather than a recorded fact."
        style={{ marginLeft: 4, color: "#999", cursor: "help" }}
      >
        *
      </span>
    </>
  );
}

// associated_meetup_group_source (see get_admin_subscriptions' doc) is
// "referral" when the value came directly from the subscriber's own
// source_group metadata (a tracked ?src= click - the more certain signal,
// left unmarked) or "landing_page" when it was inferred instead, from the
// Meetup group tied to the landing page they subscribed through (no
// referral link was captured for them). Only the inferred case gets a
// marker, same pattern as AttributionValue's Mixpanel-estimate asterisk
// above, but a different symbol so the two markers are never confused for
// each other.
function AssociatedGroupValue({ value, viaLandingPage }) {
  if (!value) return "—";
  if (!viaLandingPage) return value;
  return (
    <>
      {value}
      <span
        title="Inferred from the landing page they subscribed through - no referral link (?src=...) was captured for this subscriber, so this is the Meetup group tied to that landing page rather than a directly tracked click."
        style={{ marginLeft: 4, color: "#999", cursor: "help" }}
      >
        †
      </span>
    </>
  );
}

// Highlights the Platform column so Meetup- and Eventbrite-sourced
// subscriptions stand out at a glance in both the stats summary and the
// main table below - bright pink-red for Meetup, bright gold for
// Eventbrite. Deliberately outside both MEETUP_GROUP_PALETTE's and
// PLACEMENT_PALETTE's hue ranges below (see their comment) so Platform's
// highlight never coincides with either.
function platformHighlightStyle(platform) {
  const value = (platform || "").toLowerCase();
  if (value.includes("meetup")) return { backgroundColor: "#EDABAE" };
  if (value.includes("eventbrite")) return { backgroundColor: "#EED7A0" };
  return null;
}

// Associated Meetup Group and Placement each get their own 16-color band of
// the hue wheel - yellow-green/teal/cyan for the group, blue/violet/magenta
// for Placement - so the two columns can never land on the same color for
// the same row, and neither overlaps Platform's red/gold above.
//
// Earlier version swept hue alone in a smooth gradient, so two DIFFERENT
// (non-colliding) indices could still look near-identical if they landed
// close together in that gradient - e.g. "london20s30s" and "london30s40s"
// got different indices but two shades of the same green, reported as
// looking like the same color. Alternating between two lightness/
// saturation tiers as the hue sweeps means neighboring indices differ in
// brightness as well as hue, not hue alone, so "different bucket" reliably
// means "looks different," not just "technically not equal."
//
// 16 buckets still can't guarantee zero collisions forever - these fields
// are open-ended (arbitrary Meetup group names, ad placements, etc.), and
// with enough distinct values some are mathematically guaranteed to share
// a bucket (pigeonhole) - but the cell's own text is always shown too, so
// color is a scan aid here, never the sole identifier.
const MEETUP_GROUP_PALETTE = [
  "#EDECB6", "#CFD897", "#DDEDB6", "#BCD897", "#CDEDB6", "#AAD897",
  "#BDEDB6", "#97D898", "#B6EDBE", "#97D8AB", "#B6EDCE", "#97D8BE",
  "#B6EDDE", "#97D8D0", "#B6EBED", "#97CCD8",
];
const PLACEMENT_PALETTE = [
  "#B6D2ED", "#97AFD8", "#B6C2ED", "#979CD8", "#BAB6ED", "#A697D8",
  "#CAB6ED", "#B897D8", "#DAB6ED", "#CB97D8", "#EAB6ED", "#D897D2",
  "#EDB6DF", "#D897BF", "#EDB6CF", "#D897AC",
];

// URL Slug is the most open-ended of these columns (a distinct value per
// landing page, likely to grow well past the group/Placement columns'
// smaller enums), and between them the group + Placement already sweep
// ~280 of 360 hue degrees - not enough room left to give URL Slug its own
// full-width, non-overlapping hue band the way Platform/group/Placement
// avoid each other. Instead it sweeps the FULL hue wheel but at a visibly
// paler, less saturated tier than either of those two, so even where a hue
// numerically coincides, the actual color reads as its own quieter family
// rather than "the same highlight as the group column."
const URL_SLUG_PALETTE = [
  "#EDD4D4", "#E0CBC2", "#EDE3D4", "#E0DDC2", "#E8EDD4", "#D1E0C2",
  "#D9EDD4", "#C2E0C5", "#D4EDDE", "#C2E0D7", "#D4EDED", "#C2D7E0",
  "#D4DEED", "#C2C5E0", "#D9D4ED", "#D1C2E0", "#E8D4ED", "#E0C2DD",
  "#EDD4E3", "#E0C2CB",
];

// Explains the two-tier attribution behind "Associated Meetup Group" -
// reused as both a standing info box (once, near the top of this tab) and
// the column header's hover tooltip.
const ASSOCIATED_GROUP_TOOLTIP =
  "The Meetup group this subscription is attributed to: their own " +
  "referral link (?src=...) if one was captured on their very first page " +
  "load, otherwise the real-world Meetup group tied to the landing page " +
  "they actually subscribed through. The referral link wins when both " +
  "are available (a directly tracked click beats an inference), but " +
  "falling back to the landing page's group when there's no referral " +
  "link means most subscriptions still land under a real group here " +
  "instead of \"(none)\".";

function AssociatedGroupNote() {
  return (
    <div style={{
      color: "#374151",
      marginBottom: "1rem",
      fontSize: "0.85rem",
      background: "#fffbeb",
      border: "1px solid #fde68a",
      borderRadius: "8px",
      padding: "10px 14px",
      maxWidth: "780px",
    }}>
      📍 <strong>Associated Meetup Group</strong> is attributed with two-tier priority: a
      subscriber's own referral link (<code>?src=...</code>) when captured on their very first
      visit, otherwise the real Meetup group tied to the landing page they actually subscribed
      through. The referral link wins when both exist (it's a directly tracked click, not an
      inference) - but falling back to the landing page's group otherwise means most subscriptions
      still land under a real group here, rather than showing "(none)".
    </div>
  );
}

// FNV-1a + a Murmur3-style finalizer - the simpler multiply-and-add hash
// this replaced gave near-identical strings (e.g. "london20s30s" vs
// "london30s40s") near-identical hashes, so they'd land in the same bucket
// far more often than chance alone would predict. This mixes bits much
// more thoroughly, so visually-similar values no longer systematically
// collide the way they did before.
function hashString(value) {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x85ebca6b);
  hash ^= hash >>> 13;
  hash = Math.imul(hash, 0xc2b2ae35);
  hash ^= hash >>> 16;
  return hash >>> 0;
}

function categoryHighlightStyle(value, palette) {
  if (!value) return null;
  const index = hashString(value) % palette.length;
  return { backgroundColor: palette[index] };
}

function formatSyncTime(isoString) {
  if (!isoString) return null;
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleString();
}

// meetup_sync reflects a background job the /api/admin/subscriptions GET
// itself kicks off (at most once every 5 min) - it never blocks the
// subscriptions list above from loading, so this only ever supplements it.
function MeetupSyncStatus({ meetupSync }) {
  if (!meetupSync || meetupSync.status === "idle") return null;

  if (meetupSync.status === "running") {
    return (
      <p style={{ fontSize: "0.9rem", color: "#555" }}>
        Filling in missing platform/source_group from Meetup RSVP data in the background...
      </p>
    );
  }

  if (meetupSync.status === "error") {
    return (
      <p style={{ fontSize: "0.9rem", color: "#a94442", background: "#f2dede", padding: "8px", borderRadius: "4px" }}>
        ⚠ Meetup sync/backfill failed ({formatSyncTime(meetupSync.last_finished_at)}): {meetupSync.last_error}
        <br />
        Subscription data below is still current from Stripe - only the automatic Meetup metadata fill-in is affected.
      </p>
    );
  }

  const summary = meetupSync.last_summary;
  return (
    <p style={{ fontSize: "0.9rem", color: "#555" }}>
      Meetup metadata sync last ran {formatSyncTime(meetupSync.last_finished_at)}
      {summary ? ` - filled ${summary.filled}, ambiguous ${summary.ambiguous}, unmatched ${summary.unmatched}` : ""}
    </p>
  );
}

const STATS_TH_STYLE = { padding: "6px 14px", textAlign: "left", borderBottom: "2px solid #dee2e6", fontSize: "0.85rem" };
const STATS_TD_STYLE = { padding: "6px 14px", borderBottom: "1px solid #eee" };

// Small "i" button next to a column header - click toggles a popover
// explaining that column's calculation. A plain `title` hover tooltip isn't
// enough for the derived metrics below (avg/percent columns), which need
// more than one line to explain clearly, and a click-to-open works on
// touch devices where hover doesn't.
function InfoIcon({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: "relative", display: "inline-block", marginLeft: "4px" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="More info"
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: "13px", height: "13px", borderRadius: "50%", border: "1px solid #9ca3af",
          background: "#fff", color: "#6b7280", fontSize: "9px", fontWeight: "bold",
          lineHeight: 1, cursor: "pointer", padding: 0, verticalAlign: "middle",
        }}
      >
        i
      </button>
      {open && (
        <div
          style={{
            position: "absolute", top: "16px", left: "0", zIndex: 20, width: "260px",
            background: "#111827", color: "#f9fafb", fontSize: "0.75rem", fontWeight: "normal",
            padding: "8px 10px", borderRadius: "6px", boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            textAlign: "left", whiteSpace: "normal", lineHeight: 1.4,
          }}
        >
          {children}
        </div>
      )}
    </span>
  );
}

// Column header with an InfoIcon attached - used by GroupGrowthStats below
// so every column there (not just the new avg/percent ones) explains its
// own calculation on click.
function ThInfo({ label, children }) {
  return (
    <th style={STATS_TH_STYLE}>
      {label}
      <InfoIcon>{children}</InfoIcon>
    </th>
  );
}

const SECONDS_PER_DAY = 60 * 60 * 24;

function formatAvgDays(days) {
  if (days == null) return "—";
  return `${Math.round(days)}d`;
}

function formatPercent(pct) {
  if (pct == null) return "—";
  return `${pct.toFixed(1)}%`;
}

// All counts here are over the *currently filtered* set, not every
// subscription ever fetched - "showing" is meant to describe what's in the
// table below right now, same as the existing "N of M" line did.
//
// The three status columns are mutually exclusive and don't necessarily sum
// to Total: a subscription can be trialing, past_due, etc. - neither
// active-and-staying, due-to-cancel, nor already cancelled - so Total (the
// group's full filtered count) can run ahead of Active + Due to Cancel +
// Cancelled.
function SubscriptionStats({ filtered, total }) {
  const groups = {};
  for (const s of filtered) {
    // See get_admin_subscriptions' associated_meetup_group doc - source_group
    // (referral link) when captured, else the landing page's Meetup group.
    const associatedGroup = s.associated_meetup_group || "(none)";
    const platform = s.platform || "(none)";
    const key = `${associatedGroup}|${platform}`;
    if (!groups[key]) {
      groups[key] = { associatedGroup, platform, active: 0, dueToCancel: 0, cancelled: 0, total: 0 };
    }
    const g = groups[key];
    g.total += 1;
    if (s.status === "canceled") {
      g.cancelled += 1;
    } else if (s.cancel_at_period_end) {
      g.dueToCancel += 1;
    } else if (s.status === "active") {
      g.active += 1;
    }
  }

  const rows = Object.values(groups).sort((a, b) => b.total - a.total);
  const totals = rows.reduce((acc, r) => ({
    active: acc.active + r.active,
    dueToCancel: acc.dueToCancel + r.dueToCancel,
    cancelled: acc.cancelled + r.cancelled,
    total: acc.total + r.total,
  }), { active: 0, dueToCancel: 0, cancelled: 0, total: 0 });

  return (
    <div style={{ marginBottom: "1rem" }}>
      <div style={{ fontSize: "0.9rem", color: "#666", marginBottom: "0.5rem" }}>
        Showing {filtered.length} of {total}
      </div>
      {rows.length > 0 && (
        <table style={{ borderCollapse: "collapse", fontSize: "0.95rem" }}>
          <thead>
            <tr style={{ backgroundColor: "#f8f9fa" }}>
              <th style={STATS_TH_STYLE}>Associated Meetup Group</th>
              <th style={STATS_TH_STYLE}>Platform</th>
              <th style={STATS_TH_STYLE}>Active (NOT due to cancel)</th>
              <th style={STATS_TH_STYLE}>Due to Cancel</th>
              <th style={STATS_TH_STYLE}>Cancelled</th>
              <th style={STATS_TH_STYLE}>Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.associatedGroup}|${r.platform}`}>
                <td style={{ ...STATS_TD_STYLE, ...categoryHighlightStyle(r.associatedGroup === "(none)" ? null : r.associatedGroup, MEETUP_GROUP_PALETTE) }}><strong>{r.associatedGroup}</strong></td>
                <td style={{ ...STATS_TD_STYLE, ...platformHighlightStyle(r.platform) }}>{r.platform}</td>
                <td style={STATS_TD_STYLE}>{r.active}</td>
                <td style={STATS_TD_STYLE}>{r.dueToCancel}</td>
                <td style={STATS_TD_STYLE}>{r.cancelled}</td>
                <td style={STATS_TD_STYLE}>{r.total}</td>
              </tr>
            ))}
            <tr style={{ fontWeight: "bold" }}>
              <td style={{ ...STATS_TD_STYLE, borderTop: "2px solid #dee2e6", borderBottom: "none" }} colSpan={2}>All</td>
              <td style={{ ...STATS_TD_STYLE, borderTop: "2px solid #dee2e6", borderBottom: "none" }}>{totals.active}</td>
              <td style={{ ...STATS_TD_STYLE, borderTop: "2px solid #dee2e6", borderBottom: "none" }}>{totals.dueToCancel}</td>
              <td style={{ ...STATS_TD_STYLE, borderTop: "2px solid #dee2e6", borderBottom: "none" }}>{totals.cancelled}</td>
              <td style={{ ...STATS_TD_STYLE, borderTop: "2px solid #dee2e6", borderBottom: "none" }}>{totals.total}</td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

// Separate from SubscriptionStats above (which groups by source_group/
// platform) since blog attribution is its own dimension - a subscription
// can be blog-attributed regardless of its source_group/platform.
//
// Grouped by first_blog_post_viewed, NOT blog_slug: blog_slug is derived
// from landing_page="blog:<slug>", which gets overwritten the moment a
// visitor picks a theme on /events (where every blog CTA sends them) - so
// it's frequently null even for a genuinely blog-attributed subscription.
// first_blog_post_viewed is stored under its own metadata key specifically
// so it survives that overwrite (see captureFirstBlogPostViewed in
// sourceTracking.js) - it's the reliable "did they view a blog post at
// some point" signal. "Via CTA click" is a subset of it: clicking a CTA
// also sets first_blog_post_viewed (the click happens on a blog post page,
// which already captured the view first), so cta count &lt;= total count for
// a given slug, never independent of it.
function BlogAttributionStats({ filtered }) {
  const bySlug = {};
  let totalBlogAttributed = 0;
  let totalViaCtaClick = 0;

  for (const s of filtered) {
    const slug = s.first_blog_post_viewed;
    if (!slug) continue;
    totalBlogAttributed += 1;
    if (s.blog_cta_clicked) totalViaCtaClick += 1;
    if (!bySlug[slug]) {
      bySlug[slug] = { slug, total: 0, viaCtaClick: 0 };
    }
    bySlug[slug].total += 1;
    if (s.blog_cta_clicked) bySlug[slug].viaCtaClick += 1;
  }

  const rows = Object.values(bySlug).sort((a, b) => b.total - a.total);

  return (
    <div style={{ marginBottom: "1.5rem", padding: "12px 16px", background: "#f8f9fa", borderRadius: "6px" }}>
      <div style={{ fontSize: "0.95rem", marginBottom: rows.length > 0 ? "0.5rem" : 0 }}>
        <strong>Blog-attributed subscriptions:</strong> {totalBlogAttributed} of {filtered.length} shown
        {totalBlogAttributed > 0 && (
          <span style={{ color: "#666" }}> ({totalViaCtaClick} via a direct CTA click, {totalBlogAttributed - totalViaCtaClick} just from viewing a post)</span>
        )}
      </div>
      {rows.length > 0 && (
        <table style={{ borderCollapse: "collapse", fontSize: "0.9rem" }}>
          <thead>
            <tr>
              <th style={STATS_TH_STYLE}>Blog Post Slug</th>
              <th style={STATS_TH_STYLE}>Subscriptions</th>
              <th style={STATS_TH_STYLE}>Via CTA Click</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.slug}>
                <td style={STATS_TD_STYLE}>{r.slug}</td>
                <td style={STATS_TD_STYLE}>{r.total}</td>
                <td style={STATS_TD_STYLE}>{r.viaCtaClick}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Cross-tab: for each subscriber, which Meetup group's referral link did
// they click (Source Group - a row) vs which Meetup group's landing page
// did they actually subscribe through (raw meetup_urlname - a column)? Off-
// diagonal cells are the interesting case: e.g. a "tech" group's own link
// drives clicks, but those visitors browse around and end up subscribing
// via a "socials" group's landing page instead - a group that's a strong
// click driver isn't necessarily the one converting the actual
// subscription. Both dimensions still matter on their own (this is exactly
// why Associated Meetup Group falls back to the landing page's group
// rather than crediting neither), so this table exists to show the
// disagreement directly rather than only ever picking one winner per
// subscriber, which every other table/column on this tab does.
function SourceGroupVsLandingPageGroupCrossTab({ filtered }) {
  const rowTotals = {};
  const colTotals = {};
  const cells = {};
  let grandTotal = 0;

  for (const s of filtered) {
    const row = s.source_group || "(none)";
    const col = s.meetup_urlname || "(none)";
    if (row === "(none)" && col === "(none)") continue;
    const cellKey = `${row}|${col}`;
    cells[cellKey] = (cells[cellKey] || 0) + 1;
    rowTotals[row] = (rowTotals[row] || 0) + 1;
    colTotals[col] = (colTotals[col] || 0) + 1;
    grandTotal += 1;
  }

  const rows = Object.keys(rowTotals).sort((a, b) => rowTotals[b] - rowTotals[a]);
  const cols = Object.keys(colTotals).sort((a, b) => colTotals[b] - colTotals[a]);

  return (
    <div style={{ marginBottom: "1.5rem", padding: "12px 16px", background: "#f8f9fa", borderRadius: "6px" }}>
      <div style={{ fontSize: "0.9rem", color: "#555", marginBottom: "0.5rem" }}>
        <strong>Source Group (row, referral link clicked) vs landing page Meetup Group (column,
        where they actually subscribed)</strong> - the highlighted cells are where the two agree; an
        amber off-diagonal cell means someone clicked one group's link but ended up subscribing via
        a different group's landing page.
      </div>
      {rows.length === 0 ? (
        <p style={{ color: "#9ca3af", fontSize: "0.9rem", margin: 0 }}>
          No subscriptions in the current filter have a Source Group or a landing page Meetup Group
          to compare.
        </p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", fontSize: "0.85rem" }}>
            <thead>
              <tr>
                <th style={STATS_TH_STYLE}>Source Group &darr; \ Landing Page Group &rarr;</th>
                {cols.map((col) => (
                  <th key={col} style={{ ...STATS_TH_STYLE, textAlign: "center" }}>{col}</th>
                ))}
                <th style={{ ...STATS_TH_STYLE, textAlign: "center" }}>Row Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row}>
                  <td style={STATS_TD_STYLE}><strong>{row}</strong></td>
                  {cols.map((col) => {
                    const count = cells[`${row}|${col}`] || 0;
                    const isMatch = row === col && row !== "(none)";
                    return (
                      <td
                        key={col}
                        style={{
                          ...STATS_TD_STYLE,
                          textAlign: "center",
                          background: isMatch ? "#dcfce7" : (count > 0 ? "#fef3c7" : undefined),
                          color: count === 0 ? "#d1d5db" : undefined,
                        }}
                      >
                        {count || "·"}
                      </td>
                    );
                  })}
                  <td style={{ ...STATS_TD_STYLE, textAlign: "center", fontWeight: "bold" }}>{rowTotals[row]}</td>
                </tr>
              ))}
              <tr style={{ fontWeight: "bold" }}>
                <td style={{ ...STATS_TD_STYLE, borderTop: "2px solid #dee2e6" }}>Column Total</td>
                {cols.map((col) => (
                  <td key={col} style={{ ...STATS_TD_STYLE, borderTop: "2px solid #dee2e6", textAlign: "center" }}>
                    {colTotals[col]}
                  </td>
                ))}
                <td style={{ ...STATS_TD_STYLE, borderTop: "2px solid #dee2e6", textAlign: "center" }}>{grandTotal}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Wraps a purely client-side stats section (no network fetch - everything
// here derives from the `subscriptions` list already fetched once for this
// tab) behind a click-to-reveal toggle. Unlike WeekdayStats below (which
// gates a genuine Mixpanel API call), this is purely about keeping this
// page's default view light as more breakdown tables accumulate here over
// time: rendering `expanded && children` means React never even calls the
// wrapped component's function - so its groupBy/reduce loop never runs -
// until an admin actually asks to see it.
function CollapsibleSection({ title, children }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{ marginBottom: "1.5rem" }}>
      <button
        onClick={() => setExpanded((e) => !e)}
        style={{
          background: "none",
          border: "1px solid #d1d5db",
          borderRadius: "8px",
          padding: "8px 14px",
          fontSize: "0.9rem",
          fontWeight: "600",
          color: "#374151",
          cursor: "pointer",
          marginBottom: expanded ? "0.75rem" : 0,
        }}
      >
        {expanded ? "▼" : "▶"} {title}
      </button>
      {expanded && children}
    </div>
  );
}

// "Average active subscribers added per month" is a growth-rate metric, not
// a count of new subscribers per month: for each Associated Meetup Group it
// takes the group's CURRENT active-subscriber count and divides it by how
// long the group has been recording subscriptions at all. That denominator is
// anchored to the EARLIEST and LATEST subscription ever seen for that group,
// active or not (a since-cancelled subscription still proves the group
// existed and was being sold around that time), so cancellations lower the
// numerator without shortening the denominator.
//
// Deliberately computed over the full unfiltered `subscriptions` list rather
// than the table's `filtered` view above: the UI filters (status, date
// range, etc.) are for narrowing what the row table shows, but excluding
// e.g. cancelled subscriptions via a status filter would erase exactly the
// history this metric needs to find each group's true first/last-ever
// subscription dates.
const AVG_DAYS_PER_MONTH = 30.44;
const PADDING_DAYS = 7;

// Exact calendar elapsed time (whole months + remaining days) between two
// dates, plus that span as a fractional month count for use as the
// average's divisor.
function elapsedBetween(start, end) {
  let months = (end.getFullYear() - start.getFullYear()) * 12
    + (end.getMonth() - start.getMonth());
  let anchor = new Date(start);
  anchor.setMonth(anchor.getMonth() + months);
  if (anchor > end) {
    months -= 1;
    anchor = new Date(start);
    anchor.setMonth(anchor.getMonth() + months);
  }
  const days = Math.max(0, Math.round((end - anchor) / (1000 * 60 * 60 * 24)));

  // Floor at ~1 day so a brand-new group never divides by zero.
  const fractionalMonths = Math.max(months + days / AVG_DAYS_PER_MONTH, 1 / AVG_DAYS_PER_MONTH);

  return { months, days, fractionalMonths };
}

// The recording window is padded a week on each side rather than run
// literally from the first subscription to today: a listing was typically
// already live for a bit before its first payment came in, and the most
// recent payment is often the start of an event-driven spike rather than
// its tail end. Padding smooths the ratio against a group's raw age and
// against where "now" happens to land relative to its last/next event.
//
// Deliberately NOT capped at today: capping would mean the end-padding only
// ever did anything for a group that's gone fully quiet (last payment more
// than a week old) and was a no-op for every actively-growing group (where
// the last payment is recent, so last+1wk lands in the future and gets
// clipped straight back to "today" anyway) - i.e. it would apply to exactly
// the groups least in need of spike-smoothing. Always adding the full week
// keeps the same smoothing effect regardless of a group's current activity.
function paddedRecordingWindow(firstCreatedUnix, lastCreatedUnix) {
  const start = new Date(firstCreatedUnix * 1000);
  start.setDate(start.getDate() - PADDING_DAYS);

  const end = new Date(lastCreatedUnix * 1000);
  end.setDate(end.getDate() + PADDING_DAYS);

  return elapsedBetween(start, end);
}

function formatElapsed(months, days) {
  const parts = [];
  if (months > 0) parts.push(`${months} month${months === 1 ? "" : "s"}`);
  if (days > 0 || months === 0) parts.push(`${days} day${days === 1 ? "" : "s"}`);
  return parts.join(", ");
}

const THIRTY_DAYS_SECONDS = 30 * 24 * 60 * 60;

function GroupGrowthStats({ subscriptions }) {
  const groups = {};
  let overallFirstCreated = null;
  let overallLastCreated = null;
  let overallActive = 0;
  let overallNewLast30Days = 0;
  let overallTotal = 0;
  let overallDueToCancel = 0;
  let overallCancelled = 0;
  let overallSubscribedDaysSum = 0;
  let overallCancelDaysSum = 0;
  let overallCancelDaysCount = 0;

  const thirtyDaysAgo = Date.now() / 1000 - THIRTY_DAYS_SECONDS;
  const nowUnix = Date.now() / 1000;

  for (const s of subscriptions) {
    // See get_admin_subscriptions' associated_meetup_group doc - same
    // grouping key as SubscriptionStats above.
    const associatedGroup = s.associated_meetup_group || "(none)";
    if (!groups[associatedGroup]) {
      groups[associatedGroup] = {
        associatedGroup, firstCreated: s.created, lastCreated: s.created, active: 0, newLast30Days: 0,
        total: 0, dueToCancel: 0, cancelled: 0, subscribedDaysSum: 0, cancelDaysSum: 0, cancelDaysCount: 0,
      };
    }
    const g = groups[associatedGroup];
    if (s.created < g.firstCreated) g.firstCreated = s.created;
    if (s.created > g.lastCreated) g.lastCreated = s.created;
    // Matches SubscriptionStats' "Active (NOT due to cancel)" definition
    // below - a subscription with cancel_at_period_end set is still
    // status === "active" today, but it's already on its way out, so
    // counting it here would inflate this table's "active" figures above
    // what SubscriptionStats shows for the same group.
    const isActiveNotDueToCancel = s.status === "active" && !s.cancel_at_period_end;
    if (isActiveNotDueToCancel) g.active += 1;
    if (s.created >= thirtyDaysAgo) g.newLast30Days += 1;

    // "Selected to cancel" mirrors SubscriptionStats' Due to Cancel/Cancelled
    // buckets: status 'canceled' wins, cancel_at_period_end only counts once
    // 'canceled' is ruled out.
    g.total += 1;
    const isCancelled = s.status === "canceled";
    const isDueToCancel = !isCancelled && Boolean(s.cancel_at_period_end);
    if (isCancelled) g.cancelled += 1;
    else if (isDueToCancel) g.dueToCancel += 1;

    // Avg Time Subscribed: how long every subscription in the group has
    // actually been (or was) subscribed for, as of today - canceled_at for
    // one that's ended or scheduled to end, otherwise "now" for one still
    // going with no end in sight yet.
    g.subscribedDaysSum += ((s.canceled_at || nowUnix) - s.created) / SECONDS_PER_DAY;

    // Avg Time to Cancel: only the subscriptions that have actually chosen
    // to leave (Cancelled or Due to Cancel), and only where Stripe recorded
    // a canceled_at. Stripe sets canceled_at the moment cancellation is
    // requested (i.e. when cancel_at_period_end is turned on) - not when
    // the subscription later lapses at period end - so this is already the
    // date the customer chose to cancel, not an expiry date.
    if ((isCancelled || isDueToCancel) && s.canceled_at) {
      g.cancelDaysSum += (s.canceled_at - s.created) / SECONDS_PER_DAY;
      g.cancelDaysCount += 1;
    }

    if (overallFirstCreated === null || s.created < overallFirstCreated) overallFirstCreated = s.created;
    if (overallLastCreated === null || s.created > overallLastCreated) overallLastCreated = s.created;
    if (isActiveNotDueToCancel) overallActive += 1;
    if (s.created >= thirtyDaysAgo) overallNewLast30Days += 1;
    overallTotal += 1;
    if (isCancelled) overallCancelled += 1;
    else if (isDueToCancel) overallDueToCancel += 1;
    overallSubscribedDaysSum += ((s.canceled_at || nowUnix) - s.created) / SECONDS_PER_DAY;
    if ((isCancelled || isDueToCancel) && s.canceled_at) {
      overallCancelDaysSum += (s.canceled_at - s.created) / SECONDS_PER_DAY;
      overallCancelDaysCount += 1;
    }
  }

  // "Adjusted" columns divide a raw metric by how long the group has
  // actually been live - today minus its real first-ever subscription date,
  // UNPADDED (unlike the Recording Window above, which pads a week on each
  // side for the growth-rate metric). A young group's average tenure is
  // mechanically capped by its own age, so the raw day-count columns alone
  // make a brand-new group look artificially "sticky"; dividing by days live
  // puts groups of very different ages on a comparable footing. Floored at 1
  // day so a group that only started selling today never divides by zero.
  function daysLiveFor(firstCreatedUnix) {
    return Math.max((nowUnix - firstCreatedUnix) / SECONDS_PER_DAY, 1);
  }

  const rows = Object.values(groups)
    .map((g) => {
      const window = paddedRecordingWindow(g.firstCreated, g.lastCreated);
      const daysLive = daysLiveFor(g.firstCreated);
      const avgDaysSubscribed = g.total > 0 ? g.subscribedDaysSum / g.total : null;
      const avgDaysToCancel = g.cancelDaysCount > 0 ? g.cancelDaysSum / g.cancelDaysCount : null;
      const pctSelectedToCancel = g.total > 0 ? ((g.dueToCancel + g.cancelled) / g.total) * 100 : null;
      return {
        ...g, ...window, avgPerMonth: g.active / window.fractionalMonths,
        daysLive, avgDaysSubscribed, avgDaysToCancel, pctSelectedToCancel,
        avgDaysSubscribedAdjustedPct: avgDaysSubscribed != null ? (avgDaysSubscribed / daysLive) * 100 : null,
        avgDaysToCancelAdjustedPct: avgDaysToCancel != null ? (avgDaysToCancel / daysLive) * 100 : null,
        pctSelectedToCancelAdjusted: pctSelectedToCancel != null ? (pctSelectedToCancel * 365) / daysLive : null,
      };
    })
    .sort((a, b) => b.avgPerMonth - a.avgPerMonth);

  const overallWindow = overallFirstCreated !== null ? paddedRecordingWindow(overallFirstCreated, overallLastCreated) : null;
  const overallAvg = overallWindow ? overallActive / overallWindow.fractionalMonths : 0;
  const overallDaysLive = overallFirstCreated !== null ? daysLiveFor(overallFirstCreated) : null;
  const overallAvgDaysSubscribed = overallTotal > 0 ? overallSubscribedDaysSum / overallTotal : null;
  const overallAvgDaysToCancel = overallCancelDaysCount > 0 ? overallCancelDaysSum / overallCancelDaysCount : null;
  const overallPctSelectedToCancel = overallTotal > 0 ? ((overallDueToCancel + overallCancelled) / overallTotal) * 100 : null;
  const overallAvgDaysSubscribedAdjustedPct = overallAvgDaysSubscribed != null && overallDaysLive != null
    ? (overallAvgDaysSubscribed / overallDaysLive) * 100 : null;
  const overallAvgDaysToCancelAdjustedPct = overallAvgDaysToCancel != null && overallDaysLive != null
    ? (overallAvgDaysToCancel / overallDaysLive) * 100 : null;
  const overallPctSelectedToCancelAdjusted = overallPctSelectedToCancel != null && overallDaysLive != null
    ? (overallPctSelectedToCancel * 365) / overallDaysLive : null;

  return (
    <div style={{ marginBottom: "1.5rem", padding: "12px 16px", background: "#f8f9fa", borderRadius: "6px" }}>
      <div style={{ fontSize: "0.9rem", color: "#555", marginBottom: "0.5rem" }}>
        <strong>Average active subscribers added per month</strong> - not new signups per month. For
        each group: current active subscribers (NOT due to cancel - same definition as the Active
        column below) &divide; a padded recording window running from 1 week before that group's
        first-ever subscription (the listing was likely already live) to 1 week after its most
        recent one (to catch the tail of an event-driven spike). The window itself counts
        since-cancelled subscriptions too, so cancellations don't shrink it. This padding smooths
        the figures against a group's raw age and against where "now" happens to fall relative to
        its last/next event. Click the small "i" icon in any column's header below for details on
        how that column is calculated.
      </div>
      {rows.length > 0 && (
        <table style={{ borderCollapse: "collapse", fontSize: "0.9rem" }}>
          <thead>
            <tr>
              <ThInfo label="Associated Meetup Group">
                Which Meetup group this subscription is attributed to: the referral link's target
                group when one was captured, otherwise the Meetup group tied to the landing page the
                visitor actually subscribed through.
              </ThInfo>
              <ThInfo label="First Subscription">
                The earliest subscription ever recorded for this group (any status, including
                since-cancelled ones).
              </ThInfo>
              <ThInfo label="Last Subscription">
                The most recent subscription ever recorded for this group (any status).
              </ThInfo>
              <ThInfo label="Recording Window (±1wk)">
                How long this group has been recording subscriptions: from 1 week before its first
                subscription to 1 week after its most recent one. Padded on both ends to smooth
                against the group's raw age and against event-driven spikes near "now".
              </ThInfo>
              <ThInfo label="New Subscribers (last 30 days)">
                Subscriptions created in the last 30 days, regardless of current status.
              </ThInfo>
              <ThInfo label="Active Subscribers Now (NOT due to cancel)">
                Subscriptions with status Active today that are not scheduled to cancel.
              </ThInfo>
              <ThInfo label="Avg Active Added / Month">
                Active Subscribers Now &divide; the Recording Window in months - a growth-rate
                estimate, not a literal count of new signups per month.
              </ThInfo>
              <ThInfo label="Avg Time Subscribed">
                Average time every subscription in this group has been (or was) subscribed for, as
                of today: canceled_at &minus; created for one that has ended or is scheduled to end,
                otherwise now &minus; created for one still going with no end in sight. Covers every
                subscription ever recorded for the group, any status.
              </ThInfo>
              <ThInfo label="Avg Time Subscribed (Adjusted)">
                Avg Time Subscribed &divide; Days Live, shown as a percentage of the group's
                lifetime. Days Live is today minus this group's actual first-ever subscription date
                (unpadded, unlike the Recording Window above). A group that only went live recently
                can't yet show a long average tenure no matter how "sticky" its subscribers are, so
                this adjusts for that: e.g. 50% means the average subscriber has stuck around for
                half as long as the group itself has existed, making groups of very different ages
                comparable.
              </ThInfo>
              <ThInfo label="Avg Time to Cancel">
                Average days between subscribing and choosing to cancel, for subscriptions that are
                Due to Cancel or Cancelled and have a known cancellation date. Uses Stripe's
                canceled_at, which is set the moment cancellation is requested (e.g. when
                "cancel at period end" is turned on) - not when the subscription later expires - so
                this is the date the customer acted, not an expiry date.
              </ThInfo>
              <ThInfo label="Avg Time to Cancel (Adjusted)">
                Avg Time to Cancel &divide; Days Live (today minus this group's actual first-ever
                subscription date, unpadded), shown as a percentage of the group's lifetime. Adjusts
                for how long the group has been live, the same way Avg Time Subscribed (Adjusted)
                does, so a young group's cancellations - which can't yet have taken long to happen -
                aren't mistaken for genuinely fast churn.
              </ThInfo>
              <ThInfo label="% Selected to Cancel">
                (Due to Cancel + Cancelled) &divide; Total - the share of every subscription this
                group has ever had that has chosen to cancel, whether that cancellation has already
                taken effect or is still pending.
              </ThInfo>
              <ThInfo label="% Selected to Cancel (Adjusted)">
                % Selected to Cancel annualized: pct &times; 365 &divide; Days Live (today minus this
                group's actual first-ever subscription date, unpadded). A group only recently live
                has had less time to accumulate cancellations, so its raw % Selected to Cancel looks
                artificially low next to an older group even at the same underlying churn rate.
                Annualizing projects "at this same pace, what share would cancel per year" so groups
                of different ages are comparable - it can run past 100% for a young, fast-churning
                group.
              </ThInfo>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.associatedGroup}>
                <td style={{ ...STATS_TD_STYLE, ...categoryHighlightStyle(r.associatedGroup === "(none)" ? null : r.associatedGroup, MEETUP_GROUP_PALETTE) }}><strong>{r.associatedGroup}</strong></td>
                <td style={STATS_TD_STYLE}>{formatDate(r.firstCreated)}</td>
                <td style={STATS_TD_STYLE}>{formatDate(r.lastCreated)}</td>
                <td style={STATS_TD_STYLE}>{formatElapsed(r.months, r.days)}</td>
                <td style={STATS_TD_STYLE}>{r.newLast30Days}</td>
                <td style={STATS_TD_STYLE}>{r.active}</td>
                <td style={STATS_TD_STYLE}>{r.avgPerMonth.toFixed(2)}</td>
                <td style={STATS_TD_STYLE}>{formatAvgDays(r.avgDaysSubscribed)}</td>
                <td style={STATS_TD_STYLE}>{formatPercent(r.avgDaysSubscribedAdjustedPct)}</td>
                <td style={STATS_TD_STYLE}>{formatAvgDays(r.avgDaysToCancel)}</td>
                <td style={STATS_TD_STYLE}>{formatPercent(r.avgDaysToCancelAdjustedPct)}</td>
                <td style={STATS_TD_STYLE}>{formatPercent(r.pctSelectedToCancel)}</td>
                <td style={STATS_TD_STYLE}>{formatPercent(r.pctSelectedToCancelAdjusted)}</td>
              </tr>
            ))}
            <tr style={{ fontWeight: "bold" }}>
              <td style={{ ...STATS_TD_STYLE, borderTop: "2px solid #dee2e6", borderBottom: "none" }}>All groups</td>
              <td style={{ ...STATS_TD_STYLE, borderTop: "2px solid #dee2e6", borderBottom: "none" }}>{formatDate(overallFirstCreated)}</td>
              <td style={{ ...STATS_TD_STYLE, borderTop: "2px solid #dee2e6", borderBottom: "none" }}>{formatDate(overallLastCreated)}</td>
              <td style={{ ...STATS_TD_STYLE, borderTop: "2px solid #dee2e6", borderBottom: "none" }}>{overallWindow ? formatElapsed(overallWindow.months, overallWindow.days) : "—"}</td>
              <td style={{ ...STATS_TD_STYLE, borderTop: "2px solid #dee2e6", borderBottom: "none" }}>{overallNewLast30Days}</td>
              <td style={{ ...STATS_TD_STYLE, borderTop: "2px solid #dee2e6", borderBottom: "none" }}>{overallActive}</td>
              <td style={{ ...STATS_TD_STYLE, borderTop: "2px solid #dee2e6", borderBottom: "none" }}>{overallAvg.toFixed(2)}</td>
              <td style={{ ...STATS_TD_STYLE, borderTop: "2px solid #dee2e6", borderBottom: "none" }}>{formatAvgDays(overallAvgDaysSubscribed)}</td>
              <td style={{ ...STATS_TD_STYLE, borderTop: "2px solid #dee2e6", borderBottom: "none" }}>{formatPercent(overallAvgDaysSubscribedAdjustedPct)}</td>
              <td style={{ ...STATS_TD_STYLE, borderTop: "2px solid #dee2e6", borderBottom: "none" }}>{formatAvgDays(overallAvgDaysToCancel)}</td>
              <td style={{ ...STATS_TD_STYLE, borderTop: "2px solid #dee2e6", borderBottom: "none" }}>{formatPercent(overallAvgDaysToCancelAdjustedPct)}</td>
              <td style={{ ...STATS_TD_STYLE, borderTop: "2px solid #dee2e6", borderBottom: "none" }}>{formatPercent(overallPctSelectedToCancel)}</td>
              <td style={{ ...STATS_TD_STYLE, borderTop: "2px solid #dee2e6", borderBottom: "none" }}>{formatPercent(overallPctSelectedToCancelAdjusted)}</td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

const WEEKDAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const WEEKDAY_SHORT = {
  Monday: "Mon", Tuesday: "Tue", Wednesday: "Wed", Thursday: "Thu",
  Friday: "Fri", Saturday: "Sat", Sunday: "Sun",
};

// Same hues as the Landing Page Performance tab's Views/Subscriptions/Rate
// series (AdminLandingPagePerformance.js's METRIC_COLORS/RATE_COLOR), reused
// here rather than picked fresh, so "indigo = views, emerald = new
// subscriptions, purple = a conversion rate" stays a consistent visual rule
// across every admin tab that shows these three metrics together.
const WEEKDAY_VIEWS_COLOR = "#667eea";
const WEEKDAY_SUBS_COLOR = "#10b981";
const WEEKDAY_RATE_COLOR = "#8B5FBF";

function defaultWeekdayToDate() {
  return new Date().toISOString().slice(0, 10);
}
function defaultWeekdayFromDate() {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 30);
  return d.toISOString().slice(0, 10);
}

// Buckets subscriptions created within [fromDate, toDate] (inclusive, UTC
// calendar days - matching the backend's UTC day bucketing for the paired
// Mixpanel view counts, see app.py's get_admin_views_by_weekday) by weekday.
// Deliberately client-side over the `subscriptions` list this tab already
// fetched from Stripe for the table above - no extra API call needed for
// this half of the chart.
function subscriptionsByWeekday(subscriptions, fromDate, toDate) {
  const fromMs = new Date(`${fromDate}T00:00:00Z`).getTime();
  const toMs = new Date(`${toDate}T23:59:59Z`).getTime();
  const counts = {};
  for (const day of WEEKDAY_ORDER) counts[day] = 0;
  for (const s of subscriptions) {
    const ms = s.created * 1000;
    if (ms < fromMs || ms > toMs) continue;
    const jsDay = new Date(ms).getUTCDay(); // 0=Sunday..6=Saturday
    counts[WEEKDAY_ORDER[(jsDay + 6) % 7]] += 1; // rotate to Monday-first
  }
  return counts;
}

function WeekdayTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div style={{
      background: "white", border: "1px solid #e5e7eb", borderRadius: "8px",
      padding: "10px 12px", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", fontSize: "13px",
    }}>
      <div style={{ fontWeight: "600", marginBottom: "6px", color: "#111827" }}>{label}</div>
      {payload.map((entry) => (
        <div key={entry.dataKey} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "2px 0" }}>
          <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: entry.color, flexShrink: 0 }} />
          <span style={{ color: "#6b7280" }}>{entry.name}:</span>
          <span style={{ fontWeight: "600", color: "#111827" }}>
            {entry.value == null ? "—" : entry.dataKey === "conversion_rate" ? `${entry.value}%` : entry.value}
          </span>
        </div>
      ))}
    </div>
  );
}

// Closed-form chi-square upper-tail p-value: P(X > stat) for a chi-square
// distribution, exact (no gamma-function approximation, no external stats
// library) whenever degrees of freedom is even -
//   P(X > x) = e^(-x/2) * sum_{i=0}^{df/2 - 1} (x/2)^i / i!
// Every test below compares 7 weekday buckets against a null hypothesis, so
// df is always 6 (7 categories - 1, or (7-1)*(2-1) for the 7x2 independence
// test) - always even, so this closed form always applies here.
function chiSquareUpperTailP(stat, df) {
  if (df == null || df <= 0 || df % 2 !== 0 || stat == null || stat < 0) return null;
  const terms = df / 2;
  let sum = 0;
  let term = 1; // (stat/2)^0 / 0!
  for (let i = 0; i < terms; i++) {
    sum += term;
    term *= (stat / 2) / (i + 1);
  }
  return Math.exp(-stat / 2) * sum;
}

// How many times each weekday actually falls within [fromDate, toDate]
// (inclusive, UTC calendar days). The "expected count under no day-of-week
// effect" below isn't simply total/7 unless the range happens to be an
// exact multiple of 7 days - a 30-day range has 5 of some weekdays and only
// 4 of others, and ignoring that would make an even, unbiased range look
// like a fake weekday "pattern."
function weekdayOccurrences(fromDate, toDate) {
  const counts = {};
  for (const day of WEEKDAY_ORDER) counts[day] = 0;
  const startMs = new Date(`${fromDate}T00:00:00Z`).getTime();
  const endMs = new Date(`${toDate}T00:00:00Z`).getTime();
  for (let t = startMs; t <= endMs; t += 24 * 60 * 60 * 1000) {
    const jsDay = new Date(t).getUTCDay();
    counts[WEEKDAY_ORDER[(jsDay + 6) % 7]] += 1;
  }
  return counts;
}

// Chi-square goodness-of-fit test: are these 7 weekday counts distributed
// the way you'd expect if day-of-week made no difference (proportional to
// how many times each weekday occurred in the range), or does the spread
// look like more than random noise? Returns null (test not meaningful) if
// there's no data at all, or any weekday has zero expected count.
function weekdayGoodnessOfFit(countsByDay, occurrencesByDay) {
  const total = WEEKDAY_ORDER.reduce((sum, day) => sum + countsByDay[day], 0);
  const totalDays = WEEKDAY_ORDER.reduce((sum, day) => sum + occurrencesByDay[day], 0);
  if (total <= 0 || totalDays <= 0) return null;

  let stat = 0;
  for (const day of WEEKDAY_ORDER) {
    const expected = total * (occurrencesByDay[day] / totalDays);
    if (expected <= 0) return null;
    const diff = countsByDay[day] - expected;
    stat += (diff * diff) / expected;
  }
  return { p: chiSquareUpperTailP(stat, 6) };
}

// Chi-square test of independence between weekday and converted/
// not-converted - i.e. is the conversion rate actually different by day,
// or is the wobble you see consistent with every day sharing one true rate
// and just seeing different amounts of luck? Returns null if there's no
// data, or any weekday has zero views (an undefined "expected converted"
// for that day).
function weekdayConversionIndependence(viewsByDay, subsByDay) {
  const totalViews = WEEKDAY_ORDER.reduce((sum, day) => sum + viewsByDay[day], 0);
  const totalSubs = WEEKDAY_ORDER.reduce((sum, day) => sum + subsByDay[day], 0);
  if (totalViews <= 0) return null;
  const overallRate = totalSubs / totalViews;
  if (overallRate <= 0 || overallRate >= 1) return null;

  let stat = 0;
  for (const day of WEEKDAY_ORDER) {
    const views = viewsByDay[day];
    if (views <= 0) return null;
    const subs = subsByDay[day];
    const expectedSubs = views * overallRate;
    const expectedNonSubs = views * (1 - overallRate);
    stat += ((subs - expectedSubs) ** 2) / expectedSubs;
    stat += (((views - subs) - expectedNonSubs) ** 2) / expectedNonSubs;
  }
  return { p: chiSquareUpperTailP(stat, 6) };
}

// p < 0.05 is the conventional (if arbitrary) line for "unlikely to be
// chance" - flagged as a badge rather than just printing the raw p-value,
// since "is this real" is the actual question an admin skimming this chart
// wants answered, not a statistics lecture.
function SignificanceLine({ label, result }) {
  if (!result || result.p == null) {
    return (
      <div style={{ fontSize: "0.85rem", color: "#9ca3af" }}>
        <strong>{label}:</strong> not enough data in this range to test (some weekday has zero views/count).
      </div>
    );
  }
  const isSignificant = result.p < 0.05;
  return (
    <div style={{ fontSize: "0.85rem", color: "#374151", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
      <span style={{
        display: "inline-block", padding: "2px 8px", borderRadius: "999px", fontSize: "0.72rem", fontWeight: 700,
        background: isSignificant ? "#dcfce7" : "#f3f4f6",
        color: isSignificant ? "#166534" : "#6b7280",
        minWidth: "142px", textAlign: "center",
      }}>
        {isSignificant ? "Likely a real pattern" : "Looks like random noise"}
      </span>
      <span>
        <strong>{label}</strong> — p {result.p < 0.001 ? "< 0.001" : `= ${result.p.toFixed(3)}`}
      </span>
    </div>
  );
}

// Views come from a live Mixpanel query (see adminApi.getViewsByWeekday),
// drawing on a shared, tightly rate-limited project-wide query budget (see
// mixpanel_service.py's CACHE_TTL_SECONDS comment) - unlike the rest of this
// tab (Stripe-sourced, already loaded together above at zero extra cost),
// this section deliberately does NOT fetch on mount or on every render of
// this tab. It only ever calls Mixpanel once the admin explicitly clicks
// Load (or changes the date range and reloads) - a weekday breakdown isn't
// something every visit to this tab needs, and auto-fetching it alongside
// everything else would burn through that shared budget on visits where
// nobody looks at it. An empty placeholder chart (axes with no lines) is
// shown even before that click, with a prominent overlay explaining why,
// so the section doesn't just look broken/blank while unloaded.
function WeekdayStats({ subscriptions, adminToken }) {
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [viewsByWeekday, setViewsByWeekday] = useState(null);
  const [cacheMeta, setCacheMeta] = useState(null);
  const [fromDate, setFromDate] = useState(defaultWeekdayFromDate);
  const [toDate, setToDate] = useState(defaultWeekdayToDate);

  async function load() {
    if (!adminToken) return;
    setLoading(true);
    setError("");
    try {
      const data = await adminApi.getViewsByWeekday(adminToken, fromDate, toDate);
      setViewsByWeekday(data.views_by_weekday || {});
      setCacheMeta(data.cache || null);
      setLoaded(true);
    } catch (err) {
      console.error("Error fetching views-by-weekday:", err);
      setError(err.message || "Failed to load weekday views");
    } finally {
      setLoading(false);
    }
  }

  const subsByWeekday = subscriptionsByWeekday(subscriptions, fromDate, toDate);
  const chartData = WEEKDAY_ORDER.map((day) => {
    if (!loaded || !viewsByWeekday) {
      return { day: WEEKDAY_SHORT[day], fullDay: day, views: null, subscriptions: null, conversion_rate: null };
    }
    const views = viewsByWeekday[day] || 0;
    const subs = subsByWeekday[day] || 0;
    return {
      day: WEEKDAY_SHORT[day],
      fullDay: day,
      views,
      subscriptions: subs,
      conversion_rate: views > 0 ? Math.round((subs / views) * 1000) / 10 : null,
    };
  });

  const occurrences = weekdayOccurrences(fromDate, toDate);
  const viewsSignificance = loaded && viewsByWeekday ? weekdayGoodnessOfFit(viewsByWeekday, occurrences) : null;
  const subsSignificance = loaded && viewsByWeekday ? weekdayGoodnessOfFit(subsByWeekday, occurrences) : null;
  const conversionSignificance = loaded && viewsByWeekday ? weekdayConversionIndependence(viewsByWeekday, subsByWeekday) : null;

  return (
    <div style={{ marginBottom: "1.5rem", padding: "12px 16px", background: "#f8f9fa", borderRadius: "6px" }}>
      <div style={{ fontSize: "0.9rem", color: "#555", marginBottom: "0.5rem" }}>
        <strong>Views, New Subscriptions &amp; Conversion by Day of Week</strong> - which weekday
        gets the most traffic, the most new subscriptions, and the best view&rarr;subscription
        conversion rate, for a chosen date range.
      </div>

      {loaded && (
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "flex-end", marginBottom: "0.75rem", flexWrap: "wrap" }}>
          <label style={{ fontSize: "0.85rem", color: "#374151" }}>
            From
            <br />
            <input
              type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
              style={{ padding: "6px", borderRadius: "6px", border: "1px solid #d1d5db" }}
            />
          </label>
          <label style={{ fontSize: "0.85rem", color: "#374151" }}>
            To
            <br />
            <input
              type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
              style={{ padding: "6px", borderRadius: "6px", border: "1px solid #d1d5db" }}
            />
          </label>
          <button
            onClick={load}
            disabled={loading}
            style={{
              background: loading ? "#a5b4fc" : "#667eea", color: "white", border: "none",
              padding: "8px 16px", borderRadius: "8px", fontSize: "13px", fontWeight: "600",
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Loading..." : "Reload for this range"}
          </button>
          {cacheMeta && (
            <span style={{ fontSize: "0.8rem", color: "#9ca3af" }}>
              {cacheMeta.stale ? "Showing last successful Mixpanel fetch (rate-limited right now)" : "Fresh Mixpanel data"}
              {" "}&middot; {Math.round(cacheMeta.age_seconds)}s old
            </span>
          )}
        </div>
      )}

      {error && <p style={{ color: "red" }}>{error}</p>}

      <div style={{ position: "relative" }}>
        <div style={{
          background: "white", borderRadius: "12px", padding: "16px",
          opacity: loaded ? 1 : 0.35, filter: loaded ? "none" : "grayscale(80%)",
        }}>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 12, fill: "#6b7280" }} />
              <YAxis yAxisId="views" allowDecimals={false} width={44} tick={{ fontSize: 12, fill: WEEKDAY_VIEWS_COLOR }} />
              <YAxis yAxisId="subs" orientation="right" allowDecimals={false} width={44} tick={{ fontSize: 12, fill: WEEKDAY_SUBS_COLOR }} />
              <YAxis
                yAxisId="rate" orientation="right" width={48}
                tick={{ fontSize: 12, fill: WEEKDAY_RATE_COLOR }}
                tickFormatter={(v) => `${v}%`} domain={[0, (dataMax) => Math.max(dataMax, 1)]}
              />
              <Tooltip content={<WeekdayTooltip />} />
              <Legend />
              <Line
                yAxisId="views" type="monotone" dataKey="views" name="Views" connectNulls
                stroke={WEEKDAY_VIEWS_COLOR} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }}
              />
              <Line
                yAxisId="subs" type="monotone" dataKey="subscriptions" name="New Subscriptions" connectNulls
                stroke={WEEKDAY_SUBS_COLOR} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }}
              />
              <Line
                yAxisId="rate" type="monotone" dataKey="conversion_rate" name="View → Subscription Rate" connectNulls
                stroke={WEEKDAY_RATE_COLOR} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {!loaded && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}>
            <div style={{
              background: "white", border: "2px solid #667eea", borderRadius: "12px",
              padding: "22px 30px", boxShadow: "0 6px 20px rgba(0,0,0,0.18)", textAlign: "center", maxWidth: "380px",
            }}>
              <div style={{ fontSize: "1.6rem", marginBottom: "4px" }}>📊</div>
              <div style={{ fontSize: "1.05rem", fontWeight: 700, color: "#111827", marginBottom: "6px" }}>
                Chart not loaded yet
              </div>
              <div style={{ fontSize: "0.85rem", color: "#4b5563", marginBottom: "16px", lineHeight: 1.4 }}>
                This pulls live view data from Mixpanel, which has a tight, shared rate limit across
                the whole admin panel - so it only loads when you ask for it, not every time you open
                this tab.
              </div>
              <button
                onClick={load}
                disabled={loading || !adminToken}
                style={{
                  background: loading ? "#a5b4fc" : "#667eea", color: "white", border: "none",
                  padding: "12px 28px", borderRadius: "8px", fontSize: "15px", fontWeight: "700",
                  cursor: loading ? "not-allowed" : "pointer", boxShadow: "0 2px 6px rgba(102,126,234,0.4)",
                }}
              >
                {loading ? "Loading…" : "▶ Load Weekday Chart"}
              </button>
            </div>
          </div>
        )}
      </div>

      {loaded && viewsByWeekday && (
        <div style={{ marginTop: "14px", padding: "12px 14px", background: "white", border: "1px solid #e5e7eb", borderRadius: "8px" }}>
          <div style={{ fontSize: "0.85rem", fontWeight: 700, color: "#111827", marginBottom: "8px" }}>
            Is this day-of-week pattern real, or just noise?
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <SignificanceLine label="Views" result={viewsSignificance} />
            <SignificanceLine label="New Subscriptions" result={subsSignificance} />
            <SignificanceLine label="Conversion Rate" result={conversionSignificance} />
          </div>
          <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginTop: "8px" }}>
            Chi-square test against "no day-of-week effect" (expected counts weighted by how many
            times each weekday actually occurred in this date range) - p &lt; 0.05 flags a spread
            unlikely to be chance. Treat "Likely a real pattern" cautiously on a short date range or
            low counts, where the test itself becomes less reliable.
          </div>
        </div>
      )}
    </div>
  );
}

function AdminSubscriptions({ adminToken, tabName }) {
  const [subscriptions, setSubscriptions] = useState([]);
  const [meetupSync, setMeetupSync] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // filters
  const [groupSearch, setGroupSearch] = useState("");
  const [platformFilter, setPlatformFilter] = useState("");
  const [placementFilter, setPlacementFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [createdFromFilter, setCreatedFromFilter] = useState("");
  const [createdToFilter, setCreatedToFilter] = useState("");

  // sorting
  const [sortColumn, setSortColumn] = useState("created");
  const [sortDirection, setSortDirection] = useState("desc");

  useEffect(() => {
    if (adminToken) fetchSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken]);

  // Polls the lightweight status-only endpoint (not the full subscriptions
  // list, which re-hits Stripe) every 4s while a background Meetup sync is
  // running, so the "syncing..." message actually updates on its own
  // instead of only refreshing on a manual page reload/Refresh click. Once
  // status stops being "running", does one full fetchSubscriptions() so any
  // newly-filled platform/source_group values show up in the table too.
  useEffect(() => {
    if (!adminToken || meetupSync?.status !== "running") return undefined;

    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 45; // ~3 minutes at 4s each - give up rather than poll forever if something hung

    async function poll() {
      attempts += 1;
      try {
        const data = await adminApi.getMeetupSyncStatus(adminToken);
        if (cancelled) return;
        const newStatus = data.meetup_sync || null;

        if (newStatus && newStatus.status === "running" && attempts < MAX_ATTEMPTS) {
          setMeetupSync(newStatus);
          timeoutId = setTimeout(poll, 4000);
        } else {
          // Finished (or gave up waiting) - refresh the actual subscription
          // rows once so any newly-filled metadata is visible immediately.
          fetchSubscriptions();
        }
      } catch (err) {
        console.error("Error polling Meetup sync status:", err);
      }
    }

    let timeoutId = setTimeout(poll, 4000);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken, meetupSync?.status]);

  async function fetchSubscriptions() {
    try {
      setLoading(true);
      setError("");
      const data = await adminApi.getSubscriptions(adminToken);
      setSubscriptions(data.subscriptions || []);
      setMeetupSync(data.meetup_sync || null);
    } catch (err) {
      console.error(err);
      setError(err.message || "Error fetching subscriptions");
    } finally {
      setLoading(false);
    }
  }

  function handleSort(column) {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  }

  function renderSortableHeader(columnKey, label, description) {
    const isActive = sortColumn === columnKey;
    const sortIndicator = isActive ? (sortDirection === "asc" ? " ↑" : " ↓") : " ⇅";
    const title = description ? `${description}\n\nClick to sort by ${label}` : `Click to sort by ${label}`;

    return (
      <th
        style={{
          textAlign: "left",
          padding: "8px",
          cursor: "pointer",
          userSelect: "none",
          fontWeight: isActive ? "bold" : "normal",
          color: isActive ? "#000" : "#333"
        }}
        onClick={() => handleSort(columnKey)}
        title={title}
      >
        {label}
        <span style={{ opacity: isActive ? 1 : 0.4, fontSize: isActive ? "1em" : "0.85em", marginLeft: "4px" }}>
          {sortIndicator}
        </span>
      </th>
    );
  }

  // Distinct platform/placement values seen, for the filter dropdowns
  const platformOptions = Array.from(
    new Set(subscriptions.map((s) => s.platform).filter(Boolean))
  ).sort();
  const placementOptions = Array.from(
    new Set(subscriptions.map((s) => s.placement).filter(Boolean))
  ).sort();

  let filtered = subscriptions.filter((s) => {
    if (groupSearch &&
        !(s.associated_meetup_group || "").toLowerCase().includes(groupSearch.toLowerCase())) {
      return false;
    }
    if (platformFilter && s.platform !== platformFilter) return false;
    if (placementFilter && s.placement !== placementFilter) return false;
    if (statusFilter && s.status !== statusFilter) return false;
    if (createdFromFilter && s.created < new Date(`${createdFromFilter}T00:00:00`).getTime() / 1000) return false;
    if (createdToFilter && s.created > new Date(`${createdToFilter}T23:59:59`).getTime() / 1000) return false;
    return true;
  });

  if (sortColumn) {
    filtered = [...filtered].sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      if (typeof aVal === "string") aVal = aVal.toLowerCase();
      if (typeof bVal === "string") bVal = bVal.toLowerCase();
      aVal = aVal ?? "";
      bVal = bVal ?? "";

      const comparison = typeof aVal === "number" && typeof bVal === "number"
        ? aVal - bVal
        : (aVal < bVal ? -1 : aVal > bVal ? 1 : 0);

      return sortDirection === "asc" ? comparison : -comparison;
    });
  }

  return (
    <div>
      <h3 style={{ textAlign: "center" }}>{tabName}</h3>
      {error && <p style={{ color: "red" }}>{error}</p>}
      <MeetupSyncStatus meetupSync={meetupSync} />

      {loading ? (
        <p>Loading subscriptions...</p>
      ) : (
        <>
          <AssociatedGroupNote />
          <SourceGroupMappingsNeedsReview adminToken={adminToken} />
          <GroupGrowthStats subscriptions={subscriptions} />
          <SubscriptionStats filtered={filtered} total={subscriptions.length} />
          <CollapsibleSection title="Source Group vs Landing Page Group (mismatch check)">
            <SourceGroupVsLandingPageGroupCrossTab filtered={filtered} />
          </CollapsibleSection>
          <CollapsibleSection title="Blog-Attributed Subscriptions">
            <BlogAttributionStats filtered={filtered} />
          </CollapsibleSection>
          <WeekdayStats subscriptions={subscriptions} adminToken={adminToken} />
        </>
      )}

      <form
        onSubmit={(e) => e.preventDefault()}
        style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginBottom: "1rem", flexWrap: "wrap" }}
      >
        <input
          type="text"
          value={groupSearch}
          onChange={(e) => setGroupSearch(e.target.value)}
          placeholder="Search Associated Meetup Group (e.g. mtn)"
          style={{ width: "220px" }}
        />
        <select value={platformFilter} onChange={(e) => setPlatformFilter(e.target.value)}>
          <option value="">All Platforms</option>
          {platformOptions.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <select value={placementFilter} onChange={(e) => setPlacementFilter(e.target.value)}>
          <option value="">All Placements</option>
          {placementOptions.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="trialing">Trialing</option>
          <option value="past_due">Past Due</option>
          <option value="canceled">Canceled</option>
          <option value="incomplete">Incomplete</option>
          <option value="incomplete_expired">Incomplete Expired</option>
          <option value="unpaid">Unpaid</option>
          <option value="paused">Paused</option>
        </select>
        <label style={{ fontSize: "0.9rem", display: "flex", alignItems: "center", gap: "0.25rem" }}>
          Created from
          <input type="date" value={createdFromFilter} onChange={(e) => setCreatedFromFilter(e.target.value)} />
        </label>
        <label style={{ fontSize: "0.9rem", display: "flex", alignItems: "center", gap: "0.25rem" }}>
          Created to
          <input type="date" value={createdToFilter} onChange={(e) => setCreatedToFilter(e.target.value)} />
        </label>
        <button onClick={fetchSubscriptions} disabled={loading}>Refresh</button>
      </form>

      {filtered.length === 0 && !loading && <p>No subscriptions found.</p>}

      {filtered.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #ccc", background: "#f0f0f0" }}>
              {renderSortableHeader("customer_email", "Customer")}
              {renderSortableHeader("status", "Status")}
              {renderSortableHeader("amount", "Amount")}
              {renderSortableHeader("platform", "Platform")}
              {renderSortableHeader("associated_meetup_group", "Associated Meetup Group", ASSOCIATED_GROUP_TOOLTIP)}
              {renderSortableHeader("placement", "Placement")}
              {renderSortableHeader("landing_page_type", "Landing Page Type")}
              {renderSortableHeader("url_slug", "URL Slug")}
              {renderSortableHeader("first_blog_post_viewed", "Blog Post Viewed")}
              {renderSortableHeader("blog_cta_clicked", "Blog CTA Clicked")}
              {renderSortableHeader("blog_slug", "Blog Slug (raw)")}
              {renderSortableHeader("created", "Created")}
              {renderSortableHeader("canceled_at", "Canceled At")}
            </tr>
          </thead>
          <tbody>
            {filtered.map((s, idx) => (
              <tr
                key={s.id}
                style={{
                  backgroundColor: idx % 2 === 0 ? "#fff" : "#f9f9f9",
                  borderBottom: "1px solid #eee",
                }}
              >
                <td style={{ padding: "8px" }}>{s.customer_email || s.customer_id || "—"}</td>
                <td style={{ padding: "8px" }}>{formatStatus(s)}</td>
                <td style={{ padding: "8px" }}>{formatAmount(s)}</td>
                <td style={{ padding: "8px", ...platformHighlightStyle(s.platform) }}>{s.platform || "—"}</td>
                <td style={{ padding: "8px", ...categoryHighlightStyle(s.associated_meetup_group, MEETUP_GROUP_PALETTE) }}>
                  <AssociatedGroupValue
                    value={s.associated_meetup_group}
                    viaLandingPage={s.associated_meetup_group_source === "landing_page"}
                  />
                </td>
                <td style={{ padding: "8px", ...categoryHighlightStyle(s.placement, PLACEMENT_PALETTE) }}>{s.placement || "—"}</td>
                <td style={{ padding: "8px", textTransform: "capitalize" }}>
                  <AttributionValue value={s.landing_page_type} source={s.attribution_source} />
                </td>
                <td style={{ padding: "8px", ...categoryHighlightStyle(s.url_slug, URL_SLUG_PALETTE) }}>
                  <AttributionValue value={s.url_slug} source={s.attribution_source} />
                </td>
                <td style={{ padding: "8px" }}>{s.first_blog_post_viewed || "—"}</td>
                <td style={{ padding: "8px" }}>{s.blog_cta_clicked || "—"}</td>
                <td style={{ padding: "8px", color: "#999" }}>
                  <AttributionValue value={s.blog_slug} source={s.attribution_source} />
                </td>
                <td style={{ padding: "8px" }}>{formatDate(s.created)}</td>
                <td style={{ padding: "8px" }}>{formatDate(s.canceled_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default AdminSubscriptions;
